/**
 * Create or edit an album (a "trip").
 *
 * One dialog for both jobs. An album is identity before it is structure — a
 * name, a glyph and a colour is what makes it findable in a sidebar of twelve,
 * so those come first and everything below them is optional. Nothing here is a
 * wizard: a name alone is a complete album.
 *
 * The emoji set is curated rather than a full picker. A free-form field invites
 * a sentence into a slot every layout assumes is one square, and a 3,000-glyph
 * grid is a worse answer to "which one says holiday" than twenty-four options.
 */

import { useId, useRef, useState } from "react";
import { useT } from "../../lib/i18n.js";
import { ACCENT_COLORS } from "@fc/core";
import type { AccentColor } from "@fc/core";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { Field } from "../../components/ui/Field.js";
import { IdentityField } from "../../components/ui/IdentityField.js";
import { Input, Textarea } from "../../components/ui/Input.js";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { CountryField } from "./CountryField.js";
// The eight accent swatches are defined once, beside the settings screen's own
// picker, and reused here rather than restated — the same reason
// CreateGroupDialog imports it.
import "../group/GroupSettingsPage.css";
import "./AlbumDialog.css";

/**
 * The shape `groups.folders` returns, derived from the client rather than
 * imported from the database package: the web app depends on the API's types,
 * never on its storage layer.
 */
export type FolderSummary = Awaited<
  ReturnType<typeof trpcClient.groups.folders.query>
>[number];

export interface AlbumDialogProps {
  groupId: string;
  /** Absent or null creates a new album; present edits that one. */
  folder?: FolderSummary | null;
  onClose: () => void;
  onSaved: (folderId: string) => void;
}

/* ──────────────────────────────── The dialog ──────────────────────────────── */

/** Anything unrecognised (or absent) falls back to the first accent. */
function toAccentColor(value: string | null | undefined): AccentColor {
  return (ACCENT_COLORS as readonly string[]).includes(value ?? "")
    ? (value as AccentColor)
    : "violet";
}

export function AlbumDialog({
  groupId,
  folder,
  onClose,
  onSaved,
}: AlbumDialogProps) {
  const t = useT();
  const formId = useId();
  const nameRef = useRef<HTMLInputElement | null>(null);

  const editing = folder != null;

  const [name, setName] = useState(folder?.name ?? "");
  const [emoji, setEmoji] = useState(folder?.emoji ?? "");
  const [accent, setAccent] = useState<AccentColor>(
    toAccentColor(folder?.accentColor),
  );
  // "No colour" is a real choice, not a missing one: the album then inherits
  // whatever the group is using, and follows it when the group changes.
  const [inheritColour, setInheritColour] = useState(
    folder?.accentColor == null,
  );
  const [description, setDescription] = useState(folder?.description ?? "");
  const [startDate, setStartDate] = useState(folder?.startDate ?? "");
  const [endDate, setEndDate] = useState(folder?.endDate ?? "");
  const [locationName, setLocationName] = useState(folder?.locationName ?? "");
  const [countryCodes, setCountryCodes] = useState<string[]>(
    folder?.countryCodes ?? [],
  );
  /*
   * Held as text, not a number, so the box can be empty. A numeric state with
   * a NaN in it while somebody is deleting the last digit is how a year field
   * ends up refusing to be cleared.
   */
  const [year, setYear] = useState(
    folder?.year != null ? String(folder.year) : "",
  );

  const [dateError, setDateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !pending;

  async function save() {
    // ISO dates compare correctly as strings, so no parsing is needed to know
    // the range is back to front.
    if (startDate && endDate && endDate < startDate) {
      setDateError(t("The end date is before the start date."));
      return;
    }

    /*
     * An empty box means "no year", not zero. Anything outside the range the
     * server accepts is rejected here so the answer arrives before the round
     * trip — a typo of 202 for 2025 is the common case.
     */
    const trimmedYear = year.trim();
    const parsedYear = trimmedYear.length > 0 ? Number(trimmedYear) : null;
    if (
      parsedYear !== null &&
      (!Number.isInteger(parsedYear) || parsedYear < 1826 || parsedYear > 2200)
    ) {
      setDateError(t("That does not look like a year."));
      return;
    }
    setDateError(null);
    setError(null);
    setPending(true);

    const trimmedDescription = description.trim();
    const trimmedLocation = locationName.trim();
    const accentColor: AccentColor | null = inheritColour ? null : accent;

    try {
      if (folder) {
        await trpcClient.groups.updateFolder.mutate({
          groupId,
          folderId: folder.id,
          name: trimmedName,
          // Cleared fields go over the wire as null, not "": an empty string
          // would come back and render as a blank line under the album name.
          description:
            trimmedDescription.length > 0 ? trimmedDescription : null,
          emoji: emoji.length > 0 ? emoji : null,
          accentColor,
          startDate: startDate.length > 0 ? startDate : null,
          endDate: endDate.length > 0 ? endDate : null,
          locationName: trimmedLocation.length > 0 ? trimmedLocation : null,
          countryCodes,
          year: parsedYear,
        });
        onSaved(folder.id);
        return;
      }

      const created = await trpcClient.groups.createFolder.mutate({
        groupId,
        name: trimmedName,
        kind: "trip",
        parentId: null,
        accentColor,
        startDate: startDate.length > 0 ? startDate : null,
        endDate: endDate.length > 0 ? endDate : null,
        // The create schema takes these as optional rather than nullable, so an
        // unset field is omitted instead of sent empty.
        ...(trimmedDescription.length > 0
          ? { description: trimmedDescription }
          : {}),
        ...(trimmedLocation.length > 0
          ? { locationName: trimmedLocation }
          : {}),
        ...(countryCodes.length > 0 ? { countryCodes } : {}),
        ...(parsedYear !== null ? { year: parsedYear } : {}),
        ...(emoji.length > 0 ? { emoji } : {}),
      });
      onSaved(created.folderId);
    } catch (caught) {
      // Includes the duplicate-name conflict, which the server already words
      // for a human: "There's already something with that name here."
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      className="fc-album-dialog"
      title={editing ? t("Edit album") : t("New album")}
      description={
        editing
          ? t("Changes show up for everyone in the group straight away.")
          : t("A place for one trip or occasion. Only the name is required.")
      }
      initialFocus={nameRef}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t("Cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={pending}
            disabled={!canSubmit}
          >
            {editing ? t("Save changes") : t("Create album")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="fc-album-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {error !== null && (
          <p className="fc-album__error" role="alert">
            {error}
          </p>
        )}

        <IdentityField
          name={name}
          onNameChange={setName}
          namePlaceholder="Barcelona 2026"
          nameRef={nameRef}
          emoji={emoji}
          onEmojiChange={setEmoji}
          colour={inheritColour ? null : accent}
          onColourChange={(next) => {
            setInheritColour(next === null);
            if (next !== null) setAccent(next);
          }}
          allowInherit
          disabled={pending}
        />

        {/*
          Shown, not hidden behind a disclosure.

          These were folded away to keep the dialog short, and the cost turned
          out to be higher than the height: a trip album's dates and where it
          was are most of what makes it an album rather than a folder, and
          behind a closed summary they were never filled in. The dialog scrolls
          instead, which is the ordinary behaviour of a form and reads as one.

          They remain optional; the labels say so.
        */}
        <div className="fc-album__more">
          <div className="fc-album__more-body">
            <Field
              label={t("Description")}
              hint={t("Optional. A line about what this album holds.")}
            >
              <Textarea
                rows={2}
                value={description}
                maxLength={1000}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>

            <div className="fc-album__dates">
              <Field label={t("Starts")} error={dateError ?? undefined}>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </Field>
              <Field label={t("Ends")}>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </Field>
            </div>

            {/*
              The year, on its own, above the exact dates.
              Almost nobody fills the dates in and almost everybody knows the
              year, and the album list is ordered by it — so asking for it
              separately is the difference between a sorted list and a list
              sorted by whoever uploaded last.
            */}
            <Field
              label={t("Year")}
              hint={t("Optional. Newest albums are listed first.")}
            >
              <Input
                value={year}
                inputMode="numeric"
                maxLength={4}
                autoComplete="off"
                placeholder="2026"
                onChange={(event) =>
                  setYear(event.target.value.replace(/[^0-9]/g, ""))
                }
              />
            </Field>

            <Field
              label={t("Location")}
              hint={t("Optional. Where this happened.")}
            >
              <Input
                value={locationName}
                maxLength={120}
                autoComplete="off"
                onChange={(event) => setLocationName(event.target.value)}
                placeholder="Barcelona, Spain"
              />
            </Field>

            {/*
              Under the location rather than instead of it. Location stays
              free text — “the cabin” is a real answer — and this is the half
              that can be counted. It reads both fields for a suggestion, so
              an album called “Słowenia 2026” does not have to be told twice.
            */}
            <CountryField
              value={countryCodes}
              onChange={setCountryCodes}
              hints={[name, locationName]}
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}
